/**
 * LIQUIDO GraphQL client
 * 
 * Every call to the backend goes through this class.
 * Here we also handle data caching.
 * And login and logout because the currently logged in user with his team is also cached.
 */

import axios from "axios"
import config from "config"
import PopulatingCache from "populating-cache"
import EventBus from "@/shared/services/event-bus.js"
/**
 * Only used for config.test.js (vitest, configSource "test"): there is no Vite dev server there to
 * route an HTTP call through, so graphQlQuery() and getGraphQLSchema() below call this in-process
 * instead. A mocked Cypress e2e run (config.mock.js, or config.development.js with mockBackend on)
 * always goes over real HTTP, answered by the Vite dev-middleware in
 * mock-backend/liquido-mock-http-server.js - see that file's module doc comment, and graphQlQuery()
 * below, for why.
 */
import { graphQlQueryMock, setAuthHeader, MOCK_GRAPHQL_SCHEMA } from "../../../mock-backend/liquido-mock-domain.js"
/** Liquido backend error codes. Must match LiquidoException.java from backend*/
import LiquidoExceptionCodes from "@/liquido/services/LiquidoExceptionCodes.js"
import { LIQUIDO_ADMIN_ROLE, jwtHasRole } from "@/liquido/services/jwt-util.js"
import { isJwtExpired } from "@/shared/services/jwt-payload.js"
import { createSessionRenewal, isPastHalfLife } from "@/shared/services/session-renewal.js"

const logNetworkCall = (level, message) => {
	EventBus.emit(EventBus.Event.MOBILE_DEBUG_LOG, { level, message })
}

const GRAPHQL = '/graphql'      // ==================== BASE PATH FOR GRAPHQL endpoint  //TODO: should that be in config.common.js ?

/*
  # Architecture design decisions in liquido-graphql-client.js

	We do not do (much) error handling here. This lies in the responsibility of the caller
	We only do simple sanity checks where we can prevent unneccessary calls to the backend.

	The class handles transparent caching of fetched data. Vue components do not directly access the cache.
	Callers can force a refetch when needed. (I thought about this decission a lot and went back and forth.
	The alternative would be to make Vue components handle the cache. An let this only be a pure GraphQL client.
	Component -> GraphQLClient -> Cache    OR   Component -> Cache -> GraphQLClient)

	This is a service class. It does not have a frontend. It does no navigation.
*/


//TODO: refactor / split into api-client.js, local-cache.js and liquido-auth.js  <= not that easy!

if (!config || !config.LIQUIDO_API_URL) {
	console.error("liquido-graphql-client: ERROR I have no config!")
} else {
	if (import.meta.env.MODE === "development") {
		console.log("liquido-graphql-client => " + config.LIQUIDO_API_URL + " in NODE_ENV=" + import.meta.env.MODE)
	}
}

/**
 * Configure axios to point to our graphQL backend - or, when config.mockBackend is on for a real
 * Vite dev server (configSource "mock"/"development", NOT vitest's "test"), to this page's own
 * origin instead, where vite-plugin-mock-backend.js answers every call for real. Ignores
 * LIQUIDO_API_URL in that case on purpose: mock mode means "talk to the dev server we are already
 * being served from," regardless of whatever real backend LIQUIDO_API_URL happens to be configured
 * for. Vitest keeps using LIQUIDO_API_URL (irrelevant, since graphQlQuery() never reaches axios for
 * GraphQL calls there) because there is no dev server for an empty baseURL to even mean anything.
 */
axios.defaults.baseURL = (config.mockBackend && config.configSource !== "test") ? "" : config.LIQUIDO_API_URL

// Default global timeout = 10 secs
axios.defaults.timeout = 10000

/** This will be called for HTTP resopnse status 2xx */
let onSuccess = (response) => {
	if (response.config.url !== GRAPHQL) logNetworkCall("debug", `${response.config?.method?.toUpperCase()} ${response.config?.url} -> ${response.status}`)
	return response
}

/** 
 * Sophisticated logging of HTTP error messages is crucial!
 * You have no idea how many times this has saved me! 
 * This will be called for any response status >=400
 * 
 * First we check if there is a network error
 * Then we check for severe errors 5xx, and log them.
 * For all others we let the normal flow continue.
 */
let onError   = (error) => {
	logNetworkCall("warn", `${error.config?.method?.toUpperCase()} ${error.config?.url} -> ${error.response?.status || error.message}`)
	if (!error.response) {
		console.warn("Network error: no response at all")
	} else 
	if (error.data && error.data.includes("ECONNREFUSED") ||
		error.response && error.response.body && error.response.body.includes("ECONNREFUSED")) {
		console.warn("ECONNREFUSED: Network connection refused.")
	} else if (error.response && error.response.status >= 400 && error.response.status < 500) { 
		console.warn("liquido-graphql-client: Invalid request (4xx):", error) 
	} else if (error.response && error.response.status >= 500) { 
		console.error("liquido-graphql-client: Internal Server Error(5xx):", error) 
	} else {
		console.error("Very strange unknown HTTP error", error)
	}

	// try to log some additional debug info
	if (error.response && error.response.data) {
		let msg = "liquido-graphql-client: error.response.data=" + JSON.stringify(error.response.data)
		if (error.response.data.liquidoErrorPayload)
			msg += "\nLiquidoErrorPayLoad:", JSON.stringify(error.response.data.liquidoErrorPayload)
		console.debug(msg)
	}

	// A REST endpoint answers a LiquidoException with { liquidoException: {...} } in the HTTP body.
	// Copy it onto the error, so that callers find it at err.liquidoException - the same place
	// graphQlQuery() puts a GraphQL error's errors[0].extensions.liquidoException. One place for both.
	if (error.response?.data?.liquidoException) {
		error.liquidoException = error.response.data.liquidoException
	}

	// The backend refused our session's JWT (HTTP 401, before GraphQL even runs): it ran out (7 days, or the
	// session's maximum of 30), or the backend's key changed. Every further call would fail the same way, so end the
	// session here, once, and let the router send the user to log in again. The caller's promise never settles: its
	// catch would only put a "technical error" popup on top of the login page.
	if (isRefusedSession(error)) {
		graphQlApi.logout()
		if (sessionExpiredHandler) sessionExpiredHandler()
		return new Promise(() => {})
	}
	return Promise.reject(error);
}

/** Set by router.js: where to go when the backend refuses the session's JWT. This client does no navigation. */
let sessionExpiredHandler = undefined
/** The last JWT the backend refused - for the calls that were already under way with it */
let refusedJwt = undefined

/**
 * This tab's session: the JWT of the current login, set by setJwt() and dropped by logout(). The teamCache holds it
 * too, but only for 10 minutes (its TTL) - which is what makes the router refresh the team with loginWithJwt. Whatever
 * must know the session for longer than that reads this one: isRefusedSession(), isAdmin() and the renewal.
 */
let sessionJwt = undefined

/**
 * Sliding renewal (src/shared/services/session-renewal.js): before a request, once the session's token is past half
 * its lifetime (3.5 of 7 days), mutation renewSession gets a fresh one - beside the request, which does not wait.
 * Only this tab's own session, and only while localStorage still holds it: a login in another tab is never renewed
 * into this one. loginWithJwt renews anyway, so it does not ask.
 */
const sessionRenewal = createSessionRenewal({
	getJwt: () => (sessionJwt && sessionJwt === localStorage.getItem(graphQlApi.LIQUIDO_JWT_KEY) ? sessionJwt : undefined),
	setJwt: jwt => graphQlApi.setJwt(jwt),
	renew: jwt => graphQlApi.renewSession(jwt),
	isDue: isPastHalfLife,
})

/**
 * Is this the backend refusing our current session - not a login attempt failing? HTTP 401 on /graphql for a
 * request that carried the session's own JWT. loginWithJwt() is not: the router guard handles its failure itself
 * (tryToAuthenticate) - also when it is the session's own token, as 10 minutes after the login.
 *
 * Until 2026-10 this compared with the teamCache's copy, which is gone 10 minutes after the login: from then on a
 * refused session showed a "technical error" popup instead of the login page.
 */
export function isRefusedSession(error) {
	if (error?.response?.status !== 401 || error.config?.url !== GRAPHQL || error.config.loginAttempt) return false
	const sent = String(error.config.headers?.Authorization || "").replace(/^Bearer /, "")
	if (!sent) return false
	if (sent === sessionJwt) refusedJwt = sent
	return sent === refusedJwt
}


axios.interceptors.request.use(request => {
	let message = `${request.method?.toUpperCase()} ${request.url}`
	if (request.url !== GRAPHQL) logNetworkCall("debug", message)
	return request
})
axios.interceptors.response.use(onSuccess, onError)

/**
 * This is the central API client that calls the backend.
 * Errors are logged here. But must be handled by the caller!
 * GraphQL always returns HTTP 200.
 * If there was an error, then the response will contain an `errors` array.
 * 
 * @param {String} graphql GraphQL Query. This is NOT JSON! This is GraphQL syntax!
 * @returns GraphQL result as specified by GraphQL-spec { data: {}, errors: [] }
 */
const graphQlQuery = function(query, variables) {
	sessionRenewal.renewIfDue()   // not awaited: the request goes out with the current token, which is still valid
	return sendGraphQl(query, variables)
}

/**
 * graphQlQuery() without asking for a renewal - for loginWithJwt(), which marks its request as a login attempt, and
 * for renewSession() itself.
 * @param {Object} requestConfig more axios config for this request, e.g. { loginAttempt: true } (see isRefusedSession)
 */
const sendGraphQl = function(query, variables, requestConfig) {
	if (config.mockBackend && config.configSource === "test") {
		// vitest: no Vite dev server exists to route an HTTP call through - call the mock in-process.
		// Cypress e2e (configSource "mock"/"development") falls through to the real axios.post()
		// below instead, same as the real backend, so cy.intercept()/cy.wait() can see it.
		setAuthHeader(axios.defaults.headers.common["Authorization"])
		return graphQlQueryMock(query, variables)
	} else {
		logNetworkCall("debug", query?.slice(0,40))
		return axios.post(GRAPHQL, { query: query, variables: variables }, requestConfig)
			.then(res => {
				// graphQL's way of returning errors, as defined in the GraphQL spec
				// https://graphql.org/learn/serving-over-http/#http-status-codes
				if (res.data && res.data.errors && res.data.errors.length > 0) {
					console.info("graphQlQuery() received liquidoException: "+JSON.stringify(res.data.errors[0]?.extensions.liquidoException), res.data)
					// add the first liquidoException to the response data, so that the caller can handle it more easily
					res.data.liquidoException = res.data.errors[0].extensions.liquidoException
					return Promise.reject(res.data)
				}
				return res.data // This is the axios HTTP "data". The graphQL response contains another "res.data.data" and the "res.data.errors" attribute. I know, it's confusing.
			})
			// No error handling here! Upstream caller is responsible to handle errors
	}
}


/** Shorthands for JQL return values */
const JQL_LOGIN_USER = `{ id name email mobilephone picture website hasWebauthn emailVerified } `
const JQL_USER = `{ id name email mobilephone picture website  } `
const JQL_TEAM_MEMBER = `{ role joinedAt user ${JQL_USER} } `
const JQL_PROPOSAL =  `{ id title description icon status createdAt numSupporters likedByCurrentUser createdBy ${JQL_USER} } `   // no "is" before likedByCurrentUser!
const JQL_POLL = `{ id title status createdAt updatedAt votingStartAt votingEndAt userAlreadyVoted numBallots membersCanAddProposals proposals ${JQL_PROPOSAL} winner ${JQL_PROPOSAL}  } `
// The duelMatrix itself is not fetched here -- Matrix cannot be serialized directly by SmallRye GraphQL,
// see getPublishedTally() below for the flattened, verifiable version of it.
const JQL_PUBLISHED_TALLY = `{ pollId proposalOrder duelMatrix winnerId winnerIds numBallots } `
const JQL_TEAM = `{ id teamName inviteCode ` +
		`members ${JQL_TEAM_MEMBER} ` +
		`polls ${JQL_POLL} } `
// The little a team switcher needs about every OTHER team the user is in - not the full JQL_TEAM,
// which would mean fetching every member and poll of every team just to draw a list of names.
const JQL_TEAM_SUMMARY = `{ id teamName } `

const JQL = {
	TEAM: JQL_TEAM,
	PROPOSAL: JQL_PROPOSAL,
	CREATE_OR_JOIN_TEAM_RESULT: `{ ` +
		`team ${JQL_TEAM} ` +
		`user ${JQL_LOGIN_USER} ` +
		`teams ${JQL_TEAM_SUMMARY} ` +
		`jwt } `,
	POLL: JQL_POLL
}



// ============ Client side cache for team and user data ================

/**
 * This fetch func loads the team data from the backend
 */
const fetchTeamFunc = function(path) {
	if (path === "team") {
		console.debug("fetchTeamFunc: Fetch own team from backend")
		let graphQL = `query { team ${JQL.TEAM} }`
		return graphQlQuery(graphQL).then(res => {
			EventBus.emit(EventBus.Event.POLLS_LOADED, res.data.team.polls)
			return res.data.team
		})
	} else {
		return Promise.reject(new Error("Invalid path fetchTeamFunc(path="+JSON.stringify(path)+")"))
	}
}

const teamsCacheConfig = {
	fetchFunc: fetchTeamFunc,
	ttl: 10*60*1000,		// 10 minutes for team cache
	referencedPathAttr: "$ref",
	idAttr: "id",
}

const teamCache = new PopulatingCache(teamsCacheConfig)

// ============ Client side cache for polls and their proposals ================

/**
 * Fetch all polls or one poll from backend
 * @param path "polls" or {poll: 4711} for one specific poll
 * @emits event POLL_LOADED or POLLS_LOADED
 */
const fetchPollFunc = function(path) {
	if (path[0] === "polls") {
		console.debug("fetchPollFunc: Fetch all polls of team from backend")
		let graphQL = `query { polls ${JQL.POLL} }`
		return graphQlQuery(graphQL).then(res => {
			EventBus.emit(EventBus.Event.POLLS_LOADED, res.data.polls)
			return res.data.polls
		})
	} else if (path[0].polls) {
		console.debug("Fetch one poll from backend: "+JSON.stringify(path))
		let pollId = path[0].polls
		let graphQL = `query poll($pollId: BigInteger!) { poll(pollId: $pollId) ${JQL.POLL} }`
		return graphQlQuery(graphQL, { pollId: Number(pollId) }).then(res => {
			EventBus.emit(EventBus.Event.POLL_LOADED, res.data.poll)  // notify listeners that ONE poll has been (re)loaded from the backend
			return res.data.poll
		})
	} else {
		return Promise.reject(new Error("Cannot fetch poll(s) at path: "+JSON.stringify(path)))
	}
}

const pollsCacheConfig = {
	fetchFunc: fetchPollFunc,
	ttl: 60*1000,				// 60 seconds for polls Cache
	referencedPathAttr: "$ref",
	idAttr: "id",
}

const pollsCache = new PopulatingCache(pollsCacheConfig)
pollsCache.put("polls", [])  // make sure there is at least an empty array, until polls are loaded from backend (after login)


/**
 * ===================== exported API methods =======================
 */
let graphQlApi = {

  // Implementation note: All API methods do not have any error handling.
	// There is only some logging in the axios interceptor above.
	// If something goes wrong, then the caller is responsible to catch()
	// and process the error.

	/** Ping the backend to check if it's alive and reachable. */
	async pingApi() {
		return graphQlQuery("query { ping }")
	},

	/**
	 * Fetch the validation rules and limits that the frontend shares with the backend.
	 * Fetched once at startup (see root-app.vue) and merged over the fallback values in
	 * config.common.js, so there is one source of truth. Anonymous on purpose: the welcome, join and
	 * login screens need these rules before anybody is logged in.
	 * @returns {Object} the settings, with the same key names config.common.js already used
	 */
	async getLiquidoConfig() {
		let graphQL = `query { liquidoConfig { usernameMinLength inviteCodeLength minPasswordLength pollTitleMinLength pollDefaultRuntimeDays minVotingMinutes maxVotingDays votingEndEarliestHour proposalTitleMinLength proposalDescriptionMinLength inviteLinkPrefix pollyNameMaxLength pictureMaxBytes pictureSize } }`
		return graphQlQuery(graphQL).then(res => res.data.liquidoConfig)
	},

	/**
	 * get GraphQL schema.
	 * This can also be used to check if the backend GraphQL API is up and running.
	 * @returns the GraphQL schema
	 */ 
	async getGraphQLSchema() {
		// vitest has no dev server to answer this - same split as in graphQlQuery()
		if (config.mockBackend && config.configSource === "test") return { status: 200, data: MOCK_GRAPHQL_SCHEMA }
		return axios.get('/graphql/schema.graphql')
	},
	

	/**
	 * Login user into team. Store JWT for future requests.
	 * Will also put currentUser, team and jwt into the `teamCache`.
	 * This is called with the response data from a createNewTeam() or jointTeam() call.
	 * @param {Object} team Team with members[] and polls[]
	 * @param {Object} user currently logged in user
	 * @param {String} jwt JsonWebToken received from server
	 * @param {Array} teams every team this user belongs to, so the client can offer a team switcher.
	 *   Optional - callers that don't have it (e.g. an older cached response shape) just don't get one.
	 */
	login(team, user, jwt, teams) {
		this.teamCache.put(this.TEAM_KEY, team)
		this.teamCache.put(this.CURRENT_USER_KEY, user)
		this.teamCache.put(this.ALL_USER_TEAMS_KEY, teams || [])
		this.putPollsIntoCache(team.polls)
		this.setJwt(jwt)
		console.debug("Login <"+user.email+"> into team '" + team.teamName  + "'")
		EventBus.emit(EventBus.Event.LOGIN, {team, user, jwt})
	},

	/**
	 * Make this the session's JWT: in memory, in localStorage for the next app start, and on every further request.
	 * Called by login() - and by the renewal, which changes nothing but the token.
	 * @param {String} jwt JsonWebToken received from the backend
	 */
	setJwt(jwt) {
		sessionJwt = jwt
		this.teamCache.put(this.JWT_KEY, jwt)
		if (localStorage != null) localStorage.setItem(this.LIQUIDO_JWT_KEY, jwt)
		axios.defaults.headers.common["Authorization"] = "Bearer " + jwt
	},

	logout() {
		sessionJwt = undefined
		axios.defaults.headers.common["Authorization"] = undefined
		let userEmail = this.getCachedUser() ? this.getCachedUser().email : ""
		console.debug("Logout <"+userEmail+">")
		if (localStorage != null) localStorage.removeItem(this.LIQUIDO_JWT_KEY)
		delete axios.defaults.headers.common["Authorization"]
		this.teamCache.emptyCache()
		this.pollsCache.emptyCache()
		EventBus.emit(EventBus.Event.LOGOUT, userEmail)
	},

	/**
	 * Switch the current session into another team of the same user.
	 * The user MUST already be logged in, and MUST be a member of that team. The backend checks both.
	 * On success this replaces the whole login state, including the JWT, exactly like a fresh login.
	 *
	 * @param {Number} teamId id of another team of the currently logged in user
	 * @returns {Object} login data with the new team, user and jwt
	 */
	async switchTeam(teamId) {
		let graphQL = `mutation switchTeam($teamId: BigInteger!) { switchTeam(teamId: $teamId) ${JQL.CREATE_OR_JOIN_TEAM_RESULT} }`
		return graphQlQuery(graphQL, { teamId: Number(teamId) })
			.then(response => {
				let res = response.data.switchTeam
				// MUST empty the polls cache BEFORE login() refills it.
				// putPollsIntoCache() only ever puts under "polls/<id>", it never removes. Without this
				// the previous team's polls would stay behind and getCachedPolls() would hand back a
				// mix of two teams' polls - in a voting app. We cannot just call logout() instead,
				// because that also drops the JWT and emits LOGOUT.
				this.pollsCache.emptyCache()
				this.login(res.team, res.user, res.jwt, res.teams)
				console.debug("Switched into team:", res.team.teamName)
				return res
			})
	},

	/**
	 * This sets a special header `jwtTokenString` which is used by the
	 * MongoDB Atlas GraphQL endpoint.
	 * @param {String} jwt JsonWebToken
	 */
	setJwtTokenString(jwt) {
		axios.defaults.headers.common["jwtTokenString"] = jwt
	},

	/* ===== Synchronous utility methods that do not call the backend ========= */

	/**
	 * Check if there currently is an authenticated user.
	 * This is called quite often and needs to be sync and fast!
	 *
	 * Not with an expired JWT: the backend refuses it with HTTP 401. Until 2026-10 this ignored the expiry, so the
	 * router guard let every navigation through and an app left open longer than the token lived (an hour, then) failed on every call.
	 */
	isAuthenticated() {
		const memoryJwt = this.teamCache.getSync(this.JWT_KEY, false)
		const persistentJwt = localStorage.getItem(this.LIQUIDO_JWT_KEY)

		return memoryJwt !== undefined &&
			memoryJwt === persistentJwt &&
			!isJwtExpired(memoryJwt) &&
			this.getCachedTeam() !== undefined &&
			this.getCachedUser() !== undefined
	},

	/**
	 * What to do when the backend refuses the session's JWT in the middle of using the app. router.js sets it.
	 * @param {Function} handler called once the session is logged out
	 */
	setSessionExpiredHandler(handler) {
		sessionExpiredHandler = handler
	},

	/** 
	 * Synchrounously get the currently logged in user from local cache.
	 * May return undefined but will not throw when value is expired.
	 * @return {Object} Currently logged in user from local cache or undefined if no one is logged in or login is expired
	 */
	getCachedUser() {
		return this.teamCache.getSync(this.CURRENT_USER_KEY, false)
	},

	/**
	 * Synchronously get the current user's team from the local cache.
	 * @returns currently logged in user (if any)
	 */
	getCachedTeam() {
		return this.teamCache.getSync(this.TEAM_KEY, false)
	},

	/**
	 * Synchronously get every team the current user belongs to, from the local cache.
	 * Used to decide whether to offer a team switcher at all - most users are in exactly one team.
	 * @returns {Array} every team of the current user, or [] if none is known
	 */
	getAllUserTeams() {
		return this.teamCache.getSync(this.ALL_USER_TEAMS_KEY, false) || []
	},

	/**
	 * Is the currently logged in user the admin of the team they are currently in?
	 *
	 * Read straight out of the JWT's "groups" claim - the very same claim the backend authorises on
	 * (JwtTokenUtils.isAdmin). That makes frontend and backend agree by construction. It also has
	 * exactly one failure mode: no token, no admin.
	 *
	 * Not a security boundary - see decodeJwtPayload. It decides what to show, never what to allow.
	 *
	 * @return true only if the current JWT carries the admin role
	 */
	isAdmin() {
		// The session's JWT, not the teamCache's copy: that one is gone 10 minutes after the login, and admin controls
		// vanished on the next re-render (until 2026-10).
		return jwtHasRole(sessionJwt, LIQUIDO_ADMIN_ROLE)
	},

	/**
	 * Put the given array of polls into the cache under their ids.
	 * This will <b>replace</b> these polls in the cache.
	 * @param {Array} pollsArray array of polls
	 */
	putPollsIntoCache(pollsArray) {
		if (!Array.isArray(pollsArray)) {
			console.warn("Need array of polls to putPollsIntoCache!")
			return
		}
		pollsArray.forEach(poll => {
			this.pollsCache.put("polls/"+poll.id, poll)
		})
		EventBus.emit(EventBus.Event.POLLS_LOADED, pollsArray)
	},

	/****************************************************************
	 * API calls against backend
	 * that can be executed anonymously, eg. for logging in
	 * create or join a team
	 *****************************************************************/

	/**
	 * Load data about the user's team. This call must be authenticated with a JWT.
	 * @returns Info about user's team
	 * @rejects When JWT is missing, invalid, expired, ...
	 */
	loginWithJwt(jwt) {
		if (!jwt) throw new Error("Need JWT to login!")
		let graphQL = `query { loginWithJwt ${JQL.CREATE_OR_JOIN_TEAM_RESULT} }`
		axios.defaults.headers.common["Authorization"] = "Bearer " + jwt
		return sendGraphQl(graphQL, undefined, { loginAttempt: true })   // its failure is the router's, see isRefusedSession
			.then(res => {
				this.login(res.data.loginWithJwt.team, res.data.loginWithJwt.user, res.data.loginWithJwt.jwt, res.data.loginWithJwt.teams)
				return res.data.loginWithJwt
			})
	},

	/**
	 * A fresh JWT for this one, with its full lifetime again - but never past the session's maximum of 30 days after the
	 * real login. Called by the sliding renewal (sessionRenewal above), not by the pages.
	 * @param {String} jwt the session's token: sent exactly, whatever the default header says meanwhile
	 * @returns {Promise<String>} the fresh token, or null when the backend refused to renew this one (no longer a
	 *   member, the session at its maximum) - it then still works until it runs out. Rejects when the backend cannot be reached.
	 */
	renewSession(jwt) {
		return sendGraphQl(`mutation renewSession { renewSession }`, undefined, { headers: { Authorization: "Bearer " + jwt } })
			.then(res => res.data.renewSession)
			.catch(err => {
				if (err?.errors || err?.liquidoException) return null   // a GraphQL answer: refused
				return Promise.reject(err)
			})
	},

	/**
	 * When an already registered user wants to login,
	 * LIQUIDO can send him a magic link via email.
	 * The user MUST have access to his own email inbox.
	 *
	 * REST, not GraphQL, for the same reason as requestPasswordReset above: the mailer deadlocks
	 * inside a blocking GraphQL resolver on this Quarkus version.
	 *
	 * @param {String} email email of a registered user
	 * @returns Promise.resolve(), when email was sent successfully
	 */
	requestEmailLoginLink(email) {
		if (!email) throw new Error("Need email to log in!")
		return axios.get('/login/requestEmailLoginLink', {
			params: { email: email }
		}).then(res => res.data)
	},

	/**
	 * Login with the magic link from the email requested via requestEmailLoginLink().
	 * The link contains the user's email and a one-time emailToken; login-page.vue reads both from
	 * the query params. The backend deletes the token on first use, so a second click fails.
	 *
	 * REST like requestEmailLoginLink, and POST so the token never ends up in access logs.
	 * Returns the same TeamDataResponse as the WebAuthn login (see login-page.vue).
	 *
	 * @param {String} email from the login link (only logged by the backend, it resolves the user from the token)
	 * @param {String} emailToken one-time token from the login link
	 * @returns {Object} login data with team, user, jwt and teams
	 * @rejects when the token is invalid, already used or expired (CANNOT_LOGIN_TOKEN_INVALID)
	 */
	loginWithEmailToken(email, emailToken) {
		if (!email) throw new Error("Need email to log in!")
		if (!emailToken) throw new Error("Need emailToken to log in!")
		return axios.post('/login/loginWithEmailToken', { email, emailToken })
			.then(response => {
				let res = response.data
				this.login(res.team, res.user, res.jwt, res.teams)
				return res
			})
	},

	/**
	 * Login with email and password
	 */
	loginWithEmailPassword(email, password) {
		if (!email) throw new Error("Need email to log in!")
		if (!password) throw new Error("Need password to log in!")
		let graphQL = `query loginWithEmailPassword($email: String!, $password: String!) { loginWithEmailPassword(email: $email, password: $password) ${JQL.CREATE_OR_JOIN_TEAM_RESULT} }`
		return graphQlQuery(graphQL, { email, password }).then(response => {
			let res = response.data.loginWithEmailPassword
			this.login(res.team, res.user, res.jwt, res.teams)
			return res
		})
	},

	/**
	 * Send the welcome mail to the user who just registered.
	 *
	 * Called by welcome-chat.vue/join-team-v2.vue right after createNewTeam or joinTeam returns,
	 * using the JWT that call just stored. Takes no parameters on purpose: recipient, team and role
	 * all come from the JWT on the backend (LoginRestAPI.sendWelcomeMail).
	 *
	 * REST, not GraphQL, for the same reason as requestPasswordReset below: the mailer deadlocks
	 * inside a blocking GraphQL resolver on this Quarkus version.
	 */
	sendWelcomeMail() {
		return axios.post('/login/welcomeMail')
			.then(res => res.data)
	},

	/**
	 * Request a password reset for a user.
	 * @param {String} email must be a registered user email
	 * @returns
	 */
	requestPasswordReset(email) {
		if (!email) throw new Error("Need email to request password reset!")
		return axios.get('/login/requestPasswordResetEmail', {
			params: { email: email }
		}).then(res => res.data)
		/*
		//MAYBE: change requestPasswordReset email back to GraphQL, once the Quarkus bug is fixed
		let graphQL = `query { requestPasswordReset(email: "${email}") }`
		return graphQlQuery(graphQL)
			.then(res => {
				return res.data.requestPasswordReset
			})
				*/
	},

	/**
	 * Set a new password with the one-time token from the reset email.
	 *
	 * POST with a JSON body, NOT a GET with query params: this carries a one-time token and a
	 * plaintext password, and query strings end up in server access logs, proxy logs and browser
	 * history. The backend only accepts POST here (LoginRestAPI.resetPassword) - a GET answers 405.
	 * The body keys must match the ResetPasswordRequest record field-for-field.
	 */
	resetPassword(email, resetPasswordToken, newPassword) {
		return axios.post('/login/resetPassword', {
			email: email,
			resetPasswordToken: resetPasswordToken,
			newPassword: newPassword
		}).then(res => res.data)
		/*
		let graphQL = `query { resetPassword(email: "${email}", resetPasswordToken: "${resetPasswordToken}", newPassword: "${newPassword}") }`
		return graphQlQuery(graphQL)
			.then(res => {
				return res.data.resetPassword
			})
		*/
	},

	/**
	 * Google OneTap Login: send the googleIdToken to the backend
	 * and receive login info in response	
	 */
	googleOneTapLogin(googleIdToken) {
		let graphQL = `query googleOneTapLogin($googleIdToken: String!) { googleOneTapLogin(googleIdToken: $googleIdToken) ${JQL.CREATE_OR_JOIN_TEAM_RESULT} }`
		return graphQlQuery(graphQL, { googleIdToken }).then(response => {
			let res = response.data.googleOneTapLogin
			this.login(res.team, res.user, res.jwt, res.teams)
			return res
		})
	},


	/**
	 * Request auth token for login. 
	 * Backend will call Twilio API to send a one time login token to the user
	 * @param mobilephone Users mobilephone that must exist in the DB
	 * @param devLoginToken (optional) TESTs can send the devLoginToken to fake the request.
	 */
	requestAuthToken(mobilephone, devLoginToken) {
		let graphQL = `query authToken($mobilephone: String!, $devLoginToken: String!) { authToken(mobilephone: $mobilephone, devLoginToken: $devLoginToken) }`
		return graphQlQuery(graphQL, { mobilephone, devLoginToken })
	},

	/**
	 * try to login with they authToken that the user has entered.
	 */
	loginWithAuthToken(mobilephone, authToken) {
		if (!mobilephone) throw new Error("Need mobilephone to log in!")
		if (!authToken) throw new Error("Need authToken to log in!")
		let graphQL = `query loginWithAuthToken($mobilephone: String!, $authToken: String!) { loginWithAuthToken(mobilephone: $mobilephone, authToken: $authToken) ${JQL.CREATE_OR_JOIN_TEAM_RESULT} }`
		return graphQlQuery(graphQL, { mobilephone, authToken }).then(response => {
			let res = response.data.loginWithAuthToken
			this.login(res.team, res.user, res.jwt, res.teams)
			return res
		})
	},

	/* ================== WebAuthn REST client ==================== */

	//TODO:  Move the WebAuthnREST client into webauthn-service.js  But then what about mocking?

	/**
	 * Check if that email is a registered user, ie. that email is known
	 * in the backend database.
	 * Will also return if that user has WebAuthn authenticators.
	 * This is used in the login flow to determine if we should show the "Login with WebAuthn" button.
	 * This method calls a special REST endpoint. No login is required to call this endpoint.
	 * 
	 * @param {String} email The user's email address
	 * @return {Object } { status: "REGISTERED", email: "<registered_email>", webauthn: true }  or { status: "UNKNOWN" } if user is not registered
	 */
	async checkLoginEmail(email) {
		if (!email) throw new Error("Email is required to check WebAuthn availability")
		return axios.get('/login/check-login-email', {
			params: { email: email }
		}).then(res => res.data)
	},

	/**
	 * Request webauthn registration options (challenge, rp, user, pubKeyCredParams)
	 * for the currently logged in user. Must be authenticated!
	 * This sets a cookie!
	 */
	async getWebAuthnRegistrationChallenge() {
		// quarkus-security-webauthn would provide it's own endpoint: /q/webauthn/register-options-challenge?username=testuser
		// But we use our own custom implementation, adapted to liquido authentication scheme via JWT.
		return axios.get(
			'/webauthn/register-options-challenge',
			{ withCredentials: true }  // required!
		).then(res => { 
				console.log("GET /webauthn/register-options-challenge", res.data)
				return res.data
			})
	},

	/**
	 * Submit attestation (credential) back to server for verification and receive final login payload
	 * Must send above cookie!
	 * @param {Object} credentialResponse JSON response as returned by getWebAuthnRegistrationChallenge()
	 * @param {String} authenticatorLabel name of this authenticator
	 * @return HTTP 204
	 */
	async submitWebAuthnRegistration(credentialResponse, authenticatorLabel) {
		return axios({
			method: 'POST',
			url: '/webauthn/register',
			params: {
				label: authenticatorLabel  // Yes POST requests may have URL query parameters!
			},
			data: credentialResponse,
			withCredentials: true  // required. Sends cookie that was set in GET /register-options-challenge back to server
		}).then(res => {
			console.log("POST /webauthn/register SUCCESSFULLY registered new authenticator", res.data)
			return res.data
		})
	},

	/**
	 * Request authentication options (challenge, allowCredentials) from backend.
	 * Typically called after validating password on server side.
	 * @param {String} email
	 * @param {String} password (optional) - server may require password to issue assertion options
	 */
	async getWebAuthnLoginChallenge(email, password) {
		if (!email) throw new Error("Need email for getting WebAuthn login-options-challenge")
		return axios.get('/webauthn/login-options-challenge', { 
			params: { email: email, password: password },
			withCredentials: true  // required!
		}).then(res => res.data)
	},

	/**
	 * Submit assertion (credential) to server for verification and receive final login payload
	 * @param {Object} credentialResponse
	 */
	async submitWebAuthnLogin(credentialResponse) {
		return axios({
			method: 'POST',
			url: '/webauthn/login',
			data: credentialResponse,
			withCredentials: true
		}).then(res => res.data)
	},

	/** 
	 * [DEV] Quick development login. This calls the REST backend!
	 * @param email users email. User must exist in team
	 * @param teamName team to login, optional for the client; the backend mock only needs email
	 * @param token valid and correct devLogin.token. Will be validated in backend. This is like a simulated SMS token.
	 * @return login data with team, user and jwt (same as a joinTeam calls)
	 */
	async devLogin(email, teamName, devLoginToken) {
		if (!["development", "test", "int"].includes(import.meta.env.MODE))
			return Promise.reject("devLogin is only allowed in NODE_ENV development, test or int")
		if (!email || !devLoginToken) 
			return Promise.reject("Need email and devLoginToken!")
		/*
		return axios({
			method: "GET", 
			url: "/dev/getJWT",
			params: {
				email: email,
				teamName: teamName,
				token: devLoginToken
			}
		*/
		let graphQL = `query devLogin($email: String!, $devLoginToken: String!) { devLogin(email: $email, devLoginToken: $devLoginToken) ${JQL.CREATE_OR_JOIN_TEAM_RESULT} }`
		return graphQlQuery(graphQL, { email, devLoginToken })
			.then(res => {
				console.log("API: devLogin <"+email+">")
				this.login(res.data.devLogin.team, res.data.devLogin.user, res.data.devLogin.jwt, res.data.devLogin.teams)
				return res.data.devLogin
			})
	},

	/**
	 * Create a new team. 
	 * @param {String} teamName name of new team
	 * @param {Object} admin first admin of new team 
	 */
	async createNewTeam(teamName, admin, password) {
		let graphQL = `mutation createNewTeam($teamName: String!, $admin: UserEntityInput!, $password: String!) { ` + 
			` createNewTeam(teamName: $teamName, admin: $admin, password: $password) ${JQL.CREATE_OR_JOIN_TEAM_RESULT} }`
		let variables = {
			teamName: teamName,
			admin: admin,
			password: password
		}
		//console.log("createNewTeam Query:\n", graphQL, "variables:\n", JSON.stringify(variables))

		return graphQlQuery(graphQL, variables)
			.then(res => {
				//console.log("CreateNewTeam returned\n", JSON.stringify(res, null, 2))
				let team = res.data.createNewTeam.team
				this.login(
					team,
					res.data.createNewTeam.user,  // admin
					res.data.createNewTeam.jwt,
					res.data.createNewTeam.teams
				)
				console.debug("Created new team:", team)
				return team
			})
			// There is deliberately no error handling here, because we can't handle the error in this method :-)
			// Only catch errors if you can do something about it. Otherwise simply let the rejection bubble up the call chain.
			// Further up some UI method will do something about the error, e.g. show an meaningful error message to the user.
	},

	/**
	 * Get info about a team that I like to join, when I have an inviteCode
	 * @param {String} inviteCode a team's inviteCode
	 * @returns the team
	 */
	async getTeamForInviteCode(inviteCode) {
		let graphQL = `query teamForInviteCode($inviteCode: String!) { teamForInviteCode(inviteCode: $inviteCode) ${JQL.TEAM} }`
		return graphQlQuery(graphQL, { inviteCode })
			.then(res => res.data.teamForInviteCode)
	},

	/**
	 * [DEV] Get the newest seed team created by the backend's TestDataCreator. Only used by
	 * _design-overview.vue to find an admin and a member it can devLogin as - the seed team's name
	 * and its admin/member emails carry a timestamp and are no longer fixed.
	 * @param {String} devLoginToken must match the backend's configured devLoginToken
	 * @returns {Object} the newest seed team, with its members
	 */
	async getNewestSeedTeam(devLoginToken) {
		// MicroProfile GraphQL strips the "get" prefix from the resolver method name, so the backend's
		// TeamGraphQL.getNewestSeedTeam() is exposed as the field "newestSeedTeam" (same as
		// getTeamForInviteCode() -> "teamForInviteCode" above).
		let graphQL = `query newestSeedTeam($devLoginToken: String!) { newestSeedTeam(devLoginToken: $devLoginToken) ${JQL.TEAM} }`
		return graphQlQuery(graphQL, { devLoginToken })
			.then(res => res.data.newestSeedTeam)
	},

	/**
	 * Join a team with inviteCode
	 * @param {String} inviteCode alphanumerical invite code that the admin of the team shared
	 * @param {Object} member user data of the new team member that want's to join
	 * @param {String} password plain text password of the new user
	 */
	async joinTeam(inviteCode, member, password) {
		//TODO: password only as a fallback for passkey
		let graphQL = `mutation joinTeam($inviteCode: String!, $member: UserEntityInput!, $password: String!) { ` + 
			` joinTeam(inviteCode: $inviteCode, member: $member, password: $password) ${JQL.CREATE_OR_JOIN_TEAM_RESULT} }`
		let variables = {
			inviteCode: inviteCode,
			member: member,
			password: password
		}
		return graphQlQuery(graphQL, variables)
			.then(res => {
				let team = res.data.joinTeam.team
				this.login(
					team,
					res.data.joinTeam.user,
					res.data.joinTeam.jwt,
					res.data.joinTeam.teams
				)
				console.debug("Joined team:", team)
				return team
			})
	},

	/**
	 * The logged-in user joins ANOTHER team with its invite code (UC 2), from the invite link.
	 * The JWT says who they are; the backend wants the registered email as proof (assertProvidedIdentityMatches)
	 * and ignores the password on this path - the mutation just declares it non-null.
	 * Like switchTeam(): the previous team's polls must leave the cache before login() puts the new team's in.
	 *
	 * @param {String} inviteCode of the team to join
	 * @returns {Object} the joined team, which is now the current one
	 */
	async joinAnotherTeam(inviteCode) {
		const user = this.getCachedUser()
		let graphQL = `mutation joinTeam($inviteCode: String!, $member: UserEntityInput!, $password: String!) { ` +
			` joinTeam(inviteCode: $inviteCode, member: $member, password: $password) ${JQL.CREATE_OR_JOIN_TEAM_RESULT} }`
		return graphQlQuery(graphQL, { inviteCode, member: { name: user?.name, email: user?.email }, password: "" })
			.then(res => {
				let joined = res.data.joinTeam
				this.pollsCache.emptyCache()
				this.login(joined.team, joined.user, joined.jwt, joined.teams)
				console.debug("Joined another team:", joined.team.teamName)
				return joined.team
			})
	},

	/**********************************************************************
	 * API calls against backend that need to be authenticated with a JWT
	 **********************************************************************/

	async createPoll(pollTitle, membersCanAddProposals) {
		let graphQL = `mutation createPoll($title: String!, $membersCanAddProposals: Boolean) { createPoll(title: $title, membersCanAddProposals: $membersCanAddProposals) ${JQL.POLL} }`
		return graphQlQuery(graphQL, { title: pollTitle, membersCanAddProposals })
			.then(res => {
				let poll = res.data.createPoll
				this.pollsCache.put("polls/"+poll.id, poll)
				console.debug("Created new poll:", poll)
				return poll
			})
	},

	/**
	 * Rename a poll. Admin only, and only while the poll is still in ELABORATION.
	 * The backend refuses everything else: a member calling this, a poll that already started,
	 * or a title that another poll in the team already uses.
	 * Will update the poll in pollsCache.
	 *
	 * @param {Number} pollId the poll to rename
	 * @param {String} title the new title. Must be unique within the team.
	 * @returns {Object} the updated poll
	 */
	async updatePoll(pollId, title) {
		let graphQL = `mutation updatePoll($pollId: BigInteger!, $title: String!) { updatePoll(pollId: $pollId, title: $title) ${JQL.POLL} }`
		return graphQlQuery(graphQL, { pollId: Number(pollId), title })
			.then(res => {
				let poll = res.data.updatePoll
				this.pollsCache.put("polls/"+poll.id, poll)
				console.debug("Renamed poll:", poll)
				return poll
			})
	},

	/**
	 * Get a poll by its ID. Will try to fetch it from the cache first.
	 * If the poll is not in the cache or expired, then it will be fetched from the backend.
	 * @param {Number} pollId poll.id to load
	 * @param {Boolean} force force a refresh from the backend
	 * @returns the poll object
	 * @rejects when pollId is not a number or poll not found
	 */
	async getPollById(pollId, force = false) {
		//console.debug("getPollById(id="+pollId+", force="+force+")")
		return this.pollsCache.get("polls/"+pollId, {
			callBackend: force ? this.pollsCache.FORCE_BACKEND_CALL : this.pollsCache.CALL_BACKEND_WHEN_EXPIRED
		})
	},

	/**
	 * Everything needed to independently recompute a FINISHED poll's result: the duel matrix and
	 * the proposal id order that indexes it. Not cached -- only fetched once, on the winner page,
	 * for a poll that can no longer change.
	 * @param {Number|String} pollId a poll in status FINISHED
	 * @returns {Promise} the PublishedTally
	 */
	async getPublishedTally(pollId) {
		let graphQL = `query publishedTally($pollId: BigInteger!) { publishedTally(pollId: $pollId) ${JQL_PUBLISHED_TALLY} }`
		return graphQlQuery(graphQL, { pollId: Number(pollId) }).then(res => res.data.publishedTally)
	},

	/** 
	 * Fetch polls from cache. This might call the backend if polls are expired
	 * @param {Boolean} force pass true, if you want to force a refresh from the backend
	 * @returns {Promise} polls from cache (or empty array)
	 */
	async getPolls(force = false) {
		return this.pollsCache.get("polls", {
			callBackend: force ? this.pollsCache.FORCE_BACKEND_CALL : this.pollsCache.CALL_BACKEND_WHEN_EXPIRED
		}).then(polls => polls || [])
	},

	/** 
	 * Synchronously get all currently cached polls.
	 * 
	 * @returns {Array} array of all locally cached polls or empty array if nothing is there
	 */
	getCachedPolls() {
		let cacheData = this.pollsCache.getCacheData()
		if (!cacheData || !cacheData.polls) return []
		return cacheData.polls
	},

	/**
	 * Add a new proposal to a poll.
	 * Keep in mind that a member may only add one proposal per poll. The backend will check this.
	 * Will update the poll in local pollsCache
	 * 
	 * @param {String} pollId poll ID
	 * @param {String} title proposal title
	 * @param {String} description proposal description
	 * @param {String} icon name of fontawesome icon (without any "fa-" prefix. Just the name)
	 * @returns {Object} the updated poll with the added proposal
	 */
	async addProposal(pollId, title, description, icon) {
		let graphQL = `mutation addProposal($pollId: BigInteger!, $title: String!, $description: String!, $icon: String!) { addProposal(pollId: $pollId, title: $title, description: $description, icon: $icon) ${JQL.POLL} }`
		return graphQlQuery(graphQL, { pollId: Number(pollId), title, description, icon })
			.then(res => {
				let poll = res.data.addProposal
				this.pollsCache.put("polls/"+poll.id, poll)
				console.debug("Added proposal to poll:", poll)
				return poll
			})
	},

	/**
	 * Edit your own proposal, as long as the poll has not started yet.
	 * Will update the poll in pollsCache.
	 *
	 * @param {String} pollId poll ID
	 * @param {String} proposalId the proposal to edit. MUST be part of that poll, and created by the caller.
	 * @param {String} title new title
	 * @param {String} description new description
	 * @param {String} icon name of fontawesome icon (without any "fa-" prefix. Just the name)
	 * @returns {Object} the updated poll
	 */
	async updateProposal(pollId, proposalId, title, description, icon) {
		let graphQL = `mutation updateProposal($pollId: BigInteger!, $proposalId: BigInteger!, $title: String!, $description: String!, $icon: String!) { updateProposal(pollId: $pollId, proposalId: $proposalId, title: $title, description: $description, icon: $icon) ${JQL.POLL} }`
		return graphQlQuery(graphQL, { pollId: Number(pollId), proposalId: Number(proposalId), title, description, icon })
			.then(res => {
				let poll = res.data.updateProposal
				this.pollsCache.put("polls/"+poll.id, poll)
				console.debug("Updated proposal in poll:", poll)
				return poll
			})
	},

	/**
	 * Delete a proposal from a poll. Admin only, and only while the poll has not started.
	 * Will update the poll in pollsCache.
	 *
	 * @param {String} pollId poll ID
	 * @param {String} proposalId the proposal to delete. MUST be part of that poll.
	 * @returns {Object} the updated poll, without that proposal
	 */
	async deleteProposal(pollId, proposalId) {
		let graphQL = `mutation deleteProposal($pollId: BigInteger!, $proposalId: BigInteger!) { deleteProposal(pollId: $pollId, proposalId: $proposalId) ${JQL.POLL} }`
		return graphQlQuery(graphQL, { pollId: Number(pollId), proposalId: Number(proposalId) })
			.then(res => {
				let poll = res.data.deleteProposal
				this.pollsCache.put("polls/"+poll.id, poll)
				console.debug("Deleted proposal from poll:", poll)
				return poll
			})
	},

	/**
	 * The logged-in user changes their own name and website - the profile page. Saved in the backend, then in the
	 * cached user. Not the email: that is the login identity, and changing it needs a confirmation of its own.
	 *
	 * @param {String} name the new (nick)name, at least config.usernameMinLength characters
	 * @param {String} website a website or profile link, or empty for none
	 * @returns {Object} the user as saved. Rejects with CANNOT_UPDATE_USER for a name too short or too long.
	 */
	async updateMyProfile(name, website) {
		let graphQL = `mutation updateMyProfile($name: String!, $website: String) { updateMyProfile(name: $name, website: $website) ${JQL_USER} }`
		return graphQlQuery(graphQL, { name: (name || "").trim(), website: (website || "").trim() || null })
			.then(res => {
				const saved = res.data.updateMyProfile
				const cached = this.getCachedUser()
				if (cached) this.teamCache.put(this.CURRENT_USER_KEY, { ...cached, ...saved })
				return saved
			})
	},

	/**
	 * The logged-in user uploads a profile picture, replacing the old one. The backend crops it to a square JPEG
	 * of config.pictureSize pixels; user.picture then points at it, "/picture/<key>" - see fetchPicture().
	 *
	 * @param {String} base64 a JPEG or PNG, base64 encoded, at most config.pictureMaxBytes
	 * @returns {Object} the user as saved. Rejects with CANNOT_UPLOAD_PICTURE for what is not a readable image or too large.
	 */
	async uploadMyPicture(base64) {
		let graphQL = `mutation uploadMyPicture($base64: String!) { uploadMyPicture(base64: $base64) ${JQL_USER} }`
		return graphQlQuery(graphQL, { base64 })
			.then(res => this.putSavedUserIntoCache(res.data.uploadMyPicture))
	},

	/** The logged-in user removes their profile picture: the app shows the default avatar again. */
	async removeMyPicture() {
		let graphQL = `mutation removeMyPicture { removeMyPicture ${JQL_USER} }`
		return graphQlQuery(graphQL)
			.then(res => this.putSavedUserIntoCache(res.data.removeMyPicture))
	},

	putSavedUserIntoCache(saved) {
		const cached = this.getCachedUser()
		if (cached) this.teamCache.put(this.CURRENT_USER_KEY, { ...cached, ...saved })
		return saved
	},

	/**
	 * Load an uploaded profile picture. It is not public: only logged-in users may see it, so it cannot be an
	 * <img src> - the browser would not send the JWT. This sends it, like every other call.
	 *
	 * @param {String} picturePath a user's `picture` from the backend, "/picture/<key>"
	 * @returns {Promise<Blob>} the JPEG
	 */
	fetchPicture(picturePath) {
		return axios.get(picturePath, { responseType: "blob" }).then(res => res.data)
	},

	/**
	 * Like ("support") a proposal in a poll.
	 * Will update the poll in pollsCache and notify listeners POLL_LOADED
	 * 
	 * @param {Number} pollId a poll
	 * @param {Number} proposalId a proposal in that poll
	 * @returns {Object} the updated poll
	 */
	async likeProposal(pollId, proposalId) {
		let graphQL = `mutation likeProposal($pollId: BigInteger!, $proposalId: BigInteger!) { likeProposal(pollId: $pollId, proposalId: $proposalId) ${JQL.POLL} }`
		return graphQlQuery(graphQL, { pollId: Number(pollId), proposalId: Number(proposalId) })
			.then(res => {
				let poll = res.data.likeProposal
				this.pollsCache.put("polls/"+poll.id, poll)
				console.debug(`User likes proposal.id=${proposalId} in poll.id=${pollId}`)
				EventBus.emit(EventBus.Event.POLL_LOADED, poll)
				return poll
			})
	},

	/**
	 * Start the voting phase of a poll. It starts now (server time) and ends at the given instant.
	 * @param {Number} pollId a poll in ELABORATION
	 * @param {Date} votingEndAt when voting ends. Sent as an ISO instant in UTC ("...Z"), so the server
	 *        needs no time zone: the admin's local choice was already converted on their device.
	 * @returns {Object} the poll, now in VOTING. Also updated in the pollsCache.
	 */
	async startVotingPhase(pollId, votingEndAt) {
		let graphQL = `mutation startVotingPhase($pollId: BigInteger!, $votingEndAt: DateTime!) { startVotingPhase(pollId: $pollId, votingEndAt: $votingEndAt) ${JQL.POLL} }`
		return graphQlQuery(graphQL, { pollId: Number(pollId), votingEndAt: new Date(votingEndAt).toISOString() })
			.then(res => {
				let poll = res.data.startVotingPhase
				this.pollsCache.put("polls/" + poll.id, poll)
				console.debug("Started voting phase of poll", poll)
				return poll
			})
	},

	/**
	 * Finish the currently runnign voting phase of a poll in VOTING.
	 * @param {Number} pollId poll.id in VOTING
	 * @returns {Object} the winning proposal
	 */
	async finishVotingPhase(pollId) {
		let graphQL = `mutation finishVotingPhase($pollId: BigInteger!) { finishVotingPhase(pollId: $pollId) ${JQL.PROPOSAL} }`
		return graphQlQuery(graphQL, { pollId: Number(pollId) })
			.then(res => {
				console.debug(`Finsihed voting phase of poll.id=${pollId}`)
				return res.data.finishVotingPhase
			})
	},

	/** Get one-time voterToken for a poll */
	async getVoterToken(pollId) {
		let graphQL = `query voterToken($pollId: BigInteger!) { voterToken(pollId: $pollId) }`
		return graphQlQuery(graphQL, { pollId: Number(pollId) }).then(res => {
			console.debug("Successfully received VoterToken for poll(id="+pollId+")")  // do not log the token!
			return res.data.voterToken
		})
	},

	async castVote(pollId, voteOrderIds, voterToken) {
		console.debug("Cast vote in poll(id="+pollId+") => ", voteOrderIds)
		let graphQL = `mutation castVote($pollId: BigInteger!, $voteOrderIds: [BigInteger]!, $voterToken: String!) { castVote(pollId: $pollId, voteOrderIds: $voteOrderIds, voterToken: $voterToken) ` +
			`{ voteCount ballot { level checksum voteOrder { id } } } }`
		return graphQlQuery(graphQL, { pollId: Number(pollId), voteOrderIds: voteOrderIds.map(Number), voterToken })
			.then(res => {
				console.debug("CastVote: Ballot was casted successfully.")
				return res.data.castVote
			})
	},

	/** Get voter's ballot if he voted already. MAY return null if not. */
	async getMyBallot(pollId) {
		let graphQL = `query myBallot($pollId: BigInteger!) { myBallot(pollId: $pollId) ` +
			`{ level checksum voteOrder { id } } }`
		return graphQlQuery(graphQL, { pollId: Number(pollId) })
			.then(res => {
				if (res.data.myBallot) {
					console.debug(`User's ballot in poll(id=${pollId}) is`, res.data.myBallot)
				} else {
					console.debug(`User has not voted yet in poll(id=${pollId})`)
				}
				return res.data.myBallot
			})
	},

	/** Verify a voter's ballot with its checksum. */
	async verifyBallot(pollId, checksum) {
		let graphQL = `query verifyBallot($pollId: BigInteger!, $checksum: String!) { verifyBallot(pollId: $pollId, checksum: $checksum) ` +
			`{ level checksum voteOrder { id } } }`
		// returns user's ballot if found
		return graphQlQuery(graphQL, { pollId: Number(pollId), checksum }).then(res => res.data.verifyBallot)
	},

	/** poll status constants (trying to have one central definition for the whole client here) */
	POLL_STATUS: {
		ALL_POLLS: undefined,  // <= used in polls.vue & polls-footer.vue for filtering
		ELABORATION: "ELABORATION",
		VOTING: "VOTING",
		FINISHED: "FINISHED"
	},

	/** client side caches */
	teamCache: teamCache,
	pollsCache: pollsCache,

	/** Keys for the above caches */
	JWT_KEY: "jwt",
	CURRENT_USER_KEY: "currentUser",       // key for current user object in teamCache
	TEAM_KEY: "team",
	ALL_USER_TEAMS_KEY: "allUserTeams",    // every team of the current user, for the team switcher
	VOTER_TOKEN_KEY: "voterToken",
	LIQUIDO_JWT_KEY: "LIQUIDO_JWT",        // JWT in localStorage

	/** default JQL queries for common models */
	JQL: JQL,

	/** Error codes from LIQUIDO backend. This is read from an automatically generated file LiquidoExceptionCodes.js */
	err: LiquidoExceptionCodes,
}

export default graphQlApi
